import { useState } from 'react'

import { F0Button, UpsellingButton as F1UpsellButton } from '@factorialco/f0-react'
import { Check } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { ProductModal } from 'modules/upselling_kit/components/ProductModal'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { useUpsell } from 'modules/upselling_kit/lib/upsellContext'
import { UpsellingKitModule } from 'modules/upselling_kit/types'

interface BaseUpsellButtonProps {
  requestSource: string
  pageSource: string
  label?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'promote' | 'outlinePromote'
  showModuleModal?: boolean
  loadingComponent?: React.ReactNode
  onClick?: () => void
  onRequest?: () => void
}

type UpsellButtonProps = UpsellingKitModule & BaseUpsellButtonProps

const UpsellButtonContent = ({
  label,
  size,
  requestSource,
  requestReason,
  featureRequested,
  moduleRequested,
  pageSource,
  showModuleModal = false,
  variant = 'promote',
  onRequest,
}: UpsellButtonProps) => {
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false)
  const { create } = useCreateUpsellRequestWithErrorHandling()

  const handleRequest = async () => {
    track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_button',
      module: moduleRequested,
    })
    if (showModuleModal) {
      setIsModuleModalOpen(true)
    } else {
      await create({
        moduleRequested,
        requestSource,
        requestReason,
        featureRequested,
        pageSource,
        upsellRequestComponentType: 'upselling_button',
      })
      onRequest?.()
    }
  }

  const handleClose = () => {
    track(trackingEvents.upsellingKit.dismissedProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_button',
      module: moduleRequested,
    })
  }

  useEffect(() => {
    track(trackingEvents.upsellingKit.viewedProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_button',
      module: moduleRequested,
    })
  }, [requestSource, moduleRequested])

  const getModalProps = () => {
    if (
      requestReason === ValidReasons.MissingFeature ||
      requestReason === ValidReasons.MissingFeatureLegacy
    ) {
      return {
        moduleRequested,
        requestSource,
        requestReason,
        featureRequested,
        pageSource,
        onClose: () => setIsModuleModalOpen(false),
        onRequest,
      } as const
    }

    return {
      moduleRequested,
      requestSource,
      requestReason,
      pageSource,
      onClose: () => setIsModuleModalOpen(false),
      onRequest,
    } as const
  }

  return (
    <>
      <F1UpsellButton
        label={label ?? i18n.t('upselling_kit.button.request_information')}
        size={size}
        variant={variant}
        showConfirmation={!showModuleModal}
        onRequest={handleRequest}
        errorMessage={{
          title: i18n.t('upselling_kit.upselling_button.error_message.title'),
          description: i18n.t('upselling_kit.upselling_button.error_message.description'),
        }}
        successMessage={{
          title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
          description: i18n.t('upselling_kit.upselling_button.success_message.description'),
        }}
        loadingState={{
          label: i18n.t('upselling_kit.upselling_button.loading_state.label'),
        }}
        nextSteps={{
          title: '',
          items: [],
        }}
        onClose={handleClose}
        closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
      />
      {isModuleModalOpen && (
        <ProductModal
          {...getModalProps()}
          isOpen={isModuleModalOpen}
          onClose={() => setIsModuleModalOpen(false)}
          upsellRequestComponentType="upselling_button"
        />
      )}
    </>
  )
}

const FallbackButton = () => {
  const { isAlreadyInstalled } = useUpsell()

  if (isAlreadyInstalled) {
    return (
      <F0Button
        variant="outline"
        label={i18n.t('product_updates.in_your_plan')}
        icon={Check}
        disabled
      />
    )
  } else {
    return null
  }
}

const UpsellButton = ({
  label,
  size,
  variant,
  fallbackComponent,
  loadingComponent,
  ...upsellProps
}: UpsellButtonProps) => {
  return (
    <UpsellGuard
      {...upsellProps}
      fallbackComponent={fallbackComponent || <FallbackButton />}
      loadingComponent={loadingComponent}
    >
      <UpsellButtonContent {...upsellProps} label={label} size={size} variant={variant} />
    </UpsellGuard>
  )
}

export { UpsellButton }
